import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Lock, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { createRole, updateRole } from "@/api/projects";
import {
  PERMISSION_CATALOG_FALLBACK,
  PERMISSION_GROUP_ORDER,
} from "@/lib/permissions";
import { useGrantablePermissions } from "@/hooks/useGrantablePermissions";
import type {
  PermissionCatalogCategory,
  PermissionEntry,
  Role,
} from "@/api/types";

export interface RoleEditDialogProps {
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, edits this role; when null, creates a new one. */
  role: Role | null;
  /** Live permission catalog from /permissions/catalog (pre-grouped by
   *  category, with descriptions). Falls back to the static flat list
   *  when the live endpoint hasn't loaded. */
  catalog: PermissionCatalogCategory[];
  onSaved: (role: Role) => void;
}

interface GroupView {
  name: string;
  items: PermissionEntry[];
}

export function RoleEditDialog({
  projectSlug,
  open,
  onOpenChange,
  role,
  catalog,
  onSaved,
}: RoleEditDialogProps) {
  const isEdit = role !== null;
  const grantable = useGrantablePermissions();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isDefault, setIsDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Authoritative set of permission keys the backend currently accepts.
  // Roles can carry stale keys from older seed migrations (e.g. plain
  // "view" / "create" from before the namespacing); without filtering,
  // those phantoms ride back through PATCH /roles and trip the
  // backend's "unknown permissions" guard. Computed here from the live
  // catalog (or the static fallback) so the dialog never sends a key
  // that isn't in the catalog.
  const catalogKeys = useMemo<Set<string>>(() => {
    const keys = new Set<string>();
    if (catalog.length > 0) {
      for (const cat of catalog) {
        for (const p of cat.permissions) keys.add(p.key);
      }
    } else {
      for (const entry of PERMISSION_CATALOG_FALLBACK) keys.add(entry.key);
    }
    return keys;
  }, [catalog]);

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? "");
    setDescription(role?.description ?? "");
    // Drop any keys on the role that aren't in the live catalog — they
    // can't be displayed (no matching checkbox), so we shouldn't ship
    // them back on save.
    const rolePerms = role?.permissions ?? [];
    setSelected(new Set(rolePerms.filter((k) => catalogKeys.has(k))));
    setIsDefault(role?.is_default ?? false);
  }, [open, role, catalogKeys]);

  const groups = useMemo<GroupView[]>(() => {
    // Live catalog from /permissions/catalog is pre-grouped by the
    // backend in the order the user should see — render as-is. When
    // it's empty (network failure / first paint), fall back to the
    // static flat list and apply the legacy client-side grouping so
    // the dialog still works.
    if (catalog.length > 0) {
      return catalog.map((cat) => ({
        name: cat.category,
        items: cat.permissions.map<PermissionEntry>((p) => ({
          key: p.key,
          group: cat.category,
          // Surface the optional description as a parenthetical hint
          // alongside the label so the existing label-only renderer
          // keeps working without a schema change.
          label: p.description
            ? `${p.label} · ${p.description}`
            : p.label,
        })),
      }));
    }
    const byGroup = new Map<string, PermissionEntry[]>();
    for (const entry of PERMISSION_CATALOG_FALLBACK) {
      const list = byGroup.get(entry.group) ?? [];
      list.push(entry);
      byGroup.set(entry.group, list);
    }
    return PERMISSION_GROUP_ORDER.flatMap<GroupView>((g) =>
      byGroup.has(g) ? [{ name: g, items: byGroup.get(g)! }] : [],
    ).concat(
      Array.from(byGroup.keys())
        .filter((g) => !PERMISSION_GROUP_ORDER.includes(g))
        .map<GroupView>((g) => ({ name: g, items: byGroup.get(g)! })),
    );
  }, [catalog]);

  function toggle(key: string, on: boolean) {
    // Locked rows can't be flipped from the UI — treat the call as a no-op
    // so a stray onCheckedChange from the disabled state never mutates.
    if (!grantable.has(key)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function toggleGroup(items: PermissionEntry[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      // Group-level toggles only touch permissions the user can grant. Locked
      // items keep whatever state they came in with — that's the whole point
      // of the lock: the admin can see the role's perms but not change them.
      for (const it of items) {
        if (!grantable.has(it.key)) continue;
        if (on) next.add(it.key);
        else next.delete(it.key);
      }
      return next;
    });
  }

  async function save() {
    if (name.trim().length === 0) {
      toast.error("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      // Defensive intersection: even with the on-load filter above, a
      // catalog refresh between open and save could expose new stale
      // keys; ship only the keys the backend currently accepts.
      const permissions = Array.from(selected).filter((k) =>
        catalogKeys.has(k),
      );
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        permissions,
        is_default: isDefault,
      };
      const saved = isEdit
        ? await updateRole(projectSlug, role!.id, payload)
        : await createRole(projectSlug, payload);
      toast.success(isEdit ? "Role updated" : "Role created");
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? "Edit role" : "New role"}
            {role?.is_system && (
              <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo-700">
                <ShieldCheck className="h-3 w-3" />
                System
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Tune the permissions granted by this role. Changes apply to every member assigned to it."
              : "Create a custom role with a tailored permission set."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Name
                <span className="ml-1 text-rose-500">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={role?.is_system}
                placeholder="Sales lead"
                className="h-10 rounded-lg border-slate-200"
              />
              {role?.is_system && (
                <p className="text-[11px] text-slate-400">
                  System role names can&rsquo;t be renamed.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Default
              </label>
              <div className="flex h-10 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/40 px-3">
                <Switch checked={isDefault} onCheckedChange={setIsDefault} />
                <span className="text-xs text-slate-600">
                  Assigned to invitees when no role is picked
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Description
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this role is for, in one or two sentences."
              className="min-h-[64px] rounded-lg border-slate-200"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-medium tracking-tight text-slate-900">
                Permissions
              </h3>
              <span className="text-xs text-slate-500">
                {selected.size} selected
              </span>
            </div>

            {groups.map((g) => {
              // Group-level toggle only considers grantable items. If every
              // grantable item in the group is on, the button switches to
              // "Deselect all" — locked items are intentionally ignored.
              const grantableItems = g.items.filter((i) =>
                grantable.has(i.key),
              );
              const allOn =
                grantableItems.length > 0 &&
                grantableItems.every((i) => selected.has(i.key));
              const someOn =
                !allOn && grantableItems.some((i) => selected.has(i.key));
              const groupHasGrantable = grantableItems.length > 0;
              return (
                <div
                  key={g.name}
                  className="rounded-xl border border-slate-200 bg-white"
                >
                  <div className="flex items-center justify-between border-b border-slate-900/[0.06] px-4 py-2.5">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      {g.name}
                    </div>
                    {groupHasGrantable && (
                      <button
                        type="button"
                        onClick={() => toggleGroup(g.items, !allOn)}
                        className="text-[11px] font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        {allOn
                          ? "Deselect all"
                          : someOn
                            ? "Select remaining"
                            : "Select all"}
                      </button>
                    )}
                  </div>
                  <ul className="divide-y divide-slate-900/[0.06]">
                    {g.items.map((it) => {
                      const on = selected.has(it.key);
                      const locked = !grantable.has(it.key);
                      return (
                        <PermissionRow
                          key={it.key}
                          entry={it}
                          checked={on}
                          locked={locked}
                          onToggle={(v) => toggle(it.key, v)}
                        />
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={submitting}
          variant="primary"
          >
            {submitting && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Re-export AlertTriangle just to keep it referenced; harmless.
export { AlertTriangle as _AlertTriangle };

interface PermissionRowProps {
  entry: PermissionEntry;
  checked: boolean;
  locked: boolean;
  onToggle: (value: boolean) => void;
}

function PermissionRow({
  entry,
  checked,
  locked,
  onToggle,
}: PermissionRowProps) {
  const row = (
    <li
      className={cn(
        "flex items-start gap-3 px-4 py-2.5 transition-colors",
        checked && !locked && "bg-indigo-50/30",
        locked && "bg-slate-50/40",
      )}
    >
      <Checkbox
        id={`perm-${entry.key}`}
        checked={checked}
        disabled={locked}
        onCheckedChange={(v) => onToggle(v === true)}
        className={cn("mt-0.5", locked && "cursor-not-allowed opacity-40")}
      />
      <label
        htmlFor={`perm-${entry.key}`}
        className={cn(
          "min-w-0 flex-1",
          locked ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        )}
      >
        <div className="flex items-center gap-1.5 text-sm text-slate-800">
          {entry.label}
          {locked && (
            <Lock className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
          )}
        </div>
        <div className="font-mono text-[10px] text-slate-400">
          {entry.key}
        </div>
      </label>
    </li>
  );

  if (!locked) return row;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs text-xs">
          You don&rsquo;t have this permission yourself, so you can&rsquo;t
          grant or revoke it.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  return "Couldn't save the role.";
}
